import React, { useState } from 'react';
import { formatFileSize } from '@cowbell/ui';
import type { MessageInfoAttachment } from '../../../api/cmail/cmail.dto';
import { useAudioPlayback } from '../hooks/useAudioPlayback';
import { useAttachmentTranscript } from '../hooks/useAttachmentTranscript';
import {
  attachmentFileName,
  attachmentUrl,
  useAttachmentErrorNotifier,
  useAttachmentOpen,
} from '../hooks/useAttachmentOpen';
import { fetchAttachmentBlobUrl } from '../utils/attachmentPreviewUtils';
import { audioMimeType } from '../utils/audioAttachmentUtils';
import { AudioAttachmentChip } from './AudioAttachmentChip';

interface AudioAttachmentChipContainerProps {
  readonly attachment: MessageInfoAttachment;
}

export const AudioAttachmentChipContainer = ({
  attachment,
}: AudioAttachmentChipContainerProps) => {
  const onDownload = useAttachmentOpen(attachment);
  const onError = useAttachmentErrorNotifier(attachment);

  // S3 serves the recording as octet-stream; re-typing the blob is what lets
  // Safari play it.
  const playback = useAudioPlayback({
    load: () =>
      fetchAttachmentBlobUrl(
        attachmentUrl(attachment),
        audioMimeType(attachment) ?? 'audio/mpeg'
      ),
    onError,
  });

  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const transcript = useAttachmentTranscript(
    attachment.docId,
    attachment.transcriptSignedUrl,
    transcriptOpen
  );

  return (
    <AudioAttachmentChip
      fileName={attachmentFileName(attachment)}
      sizeLabel={formatFileSize(Number(attachment.docSize) || 0)}
      status={playback.status}
      currentTime={playback.currentTime}
      duration={playback.duration}
      onToggle={playback.toggle}
      onSeek={playback.seek}
      onDownload={onDownload}
      hasTranscript={Boolean(attachment.transcriptSignedUrl)}
      transcriptOpen={transcriptOpen}
      transcript={transcript}
      onToggleTranscript={() => setTranscriptOpen((open) => !open)}
    />
  );
};
